import {PageContainer} from '@/components/organisms';
import PropTypes from 'prop-types';
import {useMemo, useState, useCallback} from 'react';
import {useIntl} from 'react-intl';
import {MoveStoresModal} from '@/containers/ClubPlus/Tabs/TierTab/MoveStoresModal';
import {DateTime} from 'luxon';
import {useFileUploadModal} from '@/containers/ClubPlus/Tabs/Overview/TierFileUploadModal/useFileUploadModal';
import {TierFileUploadModal} from '@/containers/ClubPlus/Tabs/Overview/TierFileUploadModal/TierFileUploadModal';
import './_tierTab.scss';
import {useTiers} from '@/hooks/useTiers';
import PeacockDataTable from '@/components/organisms/PeacockDataTable';
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper';

const cellDataMapperObj = () => ({
  storeId: {
    cellData: (ctx) => {
      const storeId = ctx?.row?.original?.storeId;
      return <span className='ml-4'>{storeId}</span>;
    }
  },
  lastUpdated: {
    cellData: ({row: {original} = {}}) => (
      <div>{original?.lastUpdated ?? original?.displayDate ?? '—'}</div>
    )
  }
});

export const TierTab = ({tierID, tierName}) => {
  const [selectedStores, setSelectedStores] = useState([]);
  const [tableResetKey, setTableResetKey] = useState(0);
  const [isSelectAll] = useState(false);
  const [deselectedStoreIds, setDeselectedStoreIds] = useState(new Set());

  let pageSize = 10;
  const {
    tiersData,
    TierColConfigData,
    pageIndex,
    setPageIndex,
    setSortingObj,
    sortingObj,
    setSearchArry,
    handleResetAll,
    isLoading,
    refetch
  } = useTiers(tierID, pageSize);

  const {formatMessage} = useIntl();

  const [moveStoresModalOpen, setMoveStoresModalOpen] = useState(false);
  const {
    isFileUploadModalOpen,
    setIsFileUploadModalOpen,
    selectedTierInfo,
    handleModalOpen,
    formData,
    setFormData
  } = useFileUploadModal();
  const {id} = selectedTierInfo;

  const handleRefetchWithCleanup = () => {
    setSelectedStores([]);
    setDeselectedStoreIds(new Set());
    setTableResetKey((prevKey) => prevKey + 1);

    return refetch().then(() => {
      setSelectedStores([]);
      setDeselectedStoreIds(new Set());
    });
  };

  const handleMoveStoresModalClose = () => {
    setMoveStoresModalOpen(false);
    handleRefetchWithCleanup();
  };

  const stores = useMemo(() => {
    if (!tiersData) return [];

    return (tiersData?.stores || []).map((store) => {
      let displayDate = '-';

      if (store.lastModifiedDate) {
        displayDate = DateTime.fromJSDate(
          new Date(store.lastModifiedDate)
        ).toFormat('yyyy-MM-dd');
      }

      return {
        ...store,
        displayDate
      };
    });
  }, [tiersData]);

  const pagination = useMemo(() => {
    if (!tiersData) return 0;
    return tiersData?.pagination;
  }, [tiersData]);

  const cellDataMappedCols = getCellDataMappedCols(
    cellDataMapperObj(),
    TierColConfigData
  );

  const handleRowSelect = useCallback((selectedRecords) => {
    const transformSelectedStores = (stores) => {
      if (!stores) return [];
      return stores.map((store) => store.storeId);
    };
    setSelectedStores(transformSelectedStores(selectedRecords));
  }, []);

  const handleAddStores = useCallback(() => {
    handleModalOpen(tierID, tierName);
  }, [handleModalOpen, tierID, tierName]);

  const handleMoveStores = useCallback(() => {
    setMoveStoresModalOpen(true);
  }, []);

  return (
    <PageContainer key={tierID}>
      <div className='stores-table'>
        <PeacockDataTable
          key={`tier-table-${tableResetKey}`}
          columns={cellDataMappedCols}
          records={stores}
          isLoading={isLoading}
          pageSize={pageSize}
          totalRecords={pagination?.totalStores}
          paginationConfig={{
            pageIndex: pageIndex > 0 ? pageIndex - 1 : 0,
            setPageIndex: (idx) => setPageIndex(idx + 1)
          }}
          sortingConfig={{
            handleSorting: setSortingObj,
            initialSortConfig: sortingObj
          }}
          filterConfig={{
            setSearchArry,
            onResetAll: handleResetAll
          }}
          selectionConfig={{
            onRowSelect: handleRowSelect
          }}
          displayConfig={{wrapperClassName: 'tier-table-datatable'}}
          toolbarConfig={{
            toolbarActions: [
              {
                id: 'add-stores',
                label: 'Add Stores',
                onClick: handleAddStores,
                variant: 'secondary',
                isDark: true,
                requiresSelection: false
              },
              {
                id: 'move-stores',
                label: 'Move Stores',
                onClick: handleMoveStores,
                variant: 'secondary',
                isDark: true,
                requiresSelection: true
              }
            ]
          }}
        />
        <div className='total-records' data-testid='total-records'>
          {formatMessage(
            {id: 'CLUB_PLUS.TIER_LIST_TABLE_TOTAL_RECORDS'},
            {totalRecords: pagination?.totalStores || 0}
          )}
        </div>
      </div>

      <TierFileUploadModal
        isVisible={isFileUploadModalOpen}
        setIsVisible={setIsFileUploadModalOpen}
        onSuccess={handleRefetchWithCleanup}
        formData={formData}
        setFormData={setFormData}
        tierID={id}
        tierName={tierName}
      />

      <MoveStoresModal
        isOpen={moveStoresModalOpen}
        closeModal={handleMoveStoresModalClose}
        selectedStores={selectedStores}
        isSelectAll={isSelectAll}
        deselectedStoreIds={deselectedStoreIds}
        tierName={tierName}
        tierID={tierID}
        onSuccess={handleRefetchWithCleanup}
      />
    </PageContainer>
  );
};

TierTab.propTypes = {
  tierID: PropTypes.string.isRequired,
  tierName: PropTypes.string.isRequired
};
